/**
 * ConsentAccordionGroup — R5 consent body (Figma 1824:168730).
 *
 * Renders the shared optional-consent layout used by both the patient (proband)
 * and comparator consent editors: an informational banner followed by up to
 * three `ConsentAccordionRow`s (Incidental Findings, Research Contact, NY Sample
 * Retention), each gated by its org config's `isApplicable`.
 *
 * Values are normalized booleans (opt-in === true). The patient/comparator
 * adapters map their canonical fields to/from these booleans. Renders nothing
 * when no consent is applicable.
 */

import type { ReactNode } from 'react'

import { AlertBanner } from '@/components/common/AlertBanner'
import { consentRequiresInteraction, isUiOrgHeadquarteredInNewYork } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'

import type { ComparatorParentConsentConfigSlice } from './comparatorParentConsentConfig'
import { comparatorConsentAnyApplicable } from './comparatorParentConsentConfig'
import { ConsentAccordionRow } from './ConsentAccordionRow'


const CONSENT_INTRO =
  'These options apply to incidental findings, research contact, and sample retention. ' +
  'Other required consents are completed separately in the Inflection Family app.'

interface ConsentAccordionGroupProps {
  configs: ComparatorParentConsentConfigSlice
  incidentalOptIn: boolean
  researchOptIn: boolean
  nyOptIn: boolean
  onIncidentalChange: (optIn: boolean) => void
  onResearchChange: (optIn: boolean) => void
  onNyChange: (optIn: boolean) => void
  /** Disable all toggles (e.g. order in progress after submit). */
  disabled?: boolean
  /**
   * Disable only the incidental-findings row, independent of {@link disabled}
   * (e.g. GEN-3484: proband opt-out locks the comparator incidental toggle).
   * Defaults to {@link disabled} when omitted.
   */
  incidentalDisabled?: boolean
  /** Optional content rendered above the incidental-findings row (e.g. GEN-3484 modified notice). */
  incidentalNotice?: ReactNode
  /**
   * Tri-state selections for `requireInteraction` sections: `null`/`undefined`
   * = unanswered (neither radio checked). When omitted the row falls back to
   * the boolean opt-in flags above (toggle mode / legacy behavior). Only
   * consulted for sections whose config sets `requireInteraction: true`.
   */
  incidentalSelection?: boolean | null
  researchSelection?: boolean | null
  nySelection?: boolean | null
  /**
   * Inline validation messages for `requireInteraction` sections that are
   * unanswered after a blocked submit. When set (and the section requires
   * interaction), the row highlights and shows the message. Absent → no error.
   */
  incidentalError?: string
  researchError?: string
  nyError?: string
}

export function ConsentAccordionGroup({
  configs,
  incidentalOptIn,
  researchOptIn,
  nyOptIn,
  onIncidentalChange,
  onResearchChange,
  onNyChange,
  disabled = false,
  incidentalDisabled,
  incidentalNotice = null,
  incidentalSelection,
  researchSelection,
  nySelection,
  incidentalError,
  researchError,
  nyError,
}: ConsentAccordionGroupProps) {
  const { config } = useUiConfig()
  if (!comparatorConsentAnyApplicable(configs)) {
    return null
  }

  const { incidental, research, ny } = configs
  const incidentalRowDisabled = incidentalDisabled ?? disabled
  // GEN: when the ordering organization is not based in New York, the NY
  // sample-retention consent collapses to just its title with the check
  // hidden until expanded (it stays applicable so the value still sends).
  // Uses the shared helper so the full "New York" name (as TRF org-address
  // data can carry) is matched, not just the "NY" abbreviation.
  const collapseNy = !isUiOrgHeadquarteredInNewYork(config)

  return (
    <div className="flex flex-col gap-6">
      <AlertBanner variant="info">{CONSENT_INTRO}</AlertBanner>
      <div className="flex flex-col gap-2">
        {incidental?.isApplicable ? (
          <>
            {incidentalNotice}
            <ConsentAccordionRow
              title={incidental.sectionTitle}
              enabled={incidentalOptIn}
              onToggle={onIncidentalChange}
              descriptionHtml={incidental.disclosureHtml}
              disabled={incidentalRowDisabled}
              requireInteraction={consentRequiresInteraction(incidental)}
              selection={incidentalSelection}
              errorMessage={incidentalError}
              optInLabel={incidental.optInLabel}
              optOutLabel={incidental.optOutLabel}
            />
          </>
        ) : null}
        {research?.isApplicable ? (
          <ConsentAccordionRow
            title={research.sectionTitle}
            enabled={researchOptIn}
            onToggle={onResearchChange}
            descriptionHtml={research.disclosureHtml}
            disabled={disabled}
            requireInteraction={consentRequiresInteraction(research)}
            selection={researchSelection}
            errorMessage={researchError}
            optInLabel={research.optInLabel}
          />
        ) : null}
        {ny?.isApplicable ? (
          <ConsentAccordionRow
            title={ny.sectionTitle}
            enabled={nyOptIn}
            onToggle={onNyChange}
            descriptionHtml={ny.disclosureHtml}
            disabled={disabled}
            hideToggleWhenCollapsed={collapseNy}
            requireInteraction={consentRequiresInteraction(ny)}
            selection={nySelection}
            errorMessage={nyError}
            optInLabel={ny.optInLabel}
          />
        ) : null}
      </div>
    </div>
  )
}
